import * as React from 'react'
import SelectRC, { Option } from 'rc-select/lib/index.js'
import { Col, Row, Modal, Button, Form, Table } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faSearch } from '@fortawesome/pro-duotone-svg-icons'
import useCompany from '../../util/useCompany'
import Pagination from '../Elements/Pagination.jsx'
import LoadingDefault from '../Elements/Loading/LoadingDefault.jsx'

interface CompanySearchFieldProps {
    changeValue: (itemId: any, value: any, item: any) => void
    conditions: any[]
    custom_layout?: any
    embedded?: any
    errors: any | null
    isNewDossier: boolean
    item: {
        id: number
        default_locale: string
        translations: any[]
        display_position_id: number
        locked: boolean
        def_dossier_id: number
        settings: any
    }
    kedo: any
    linkId?: any
    mode: 'edit' | 'view'
    onKeyPress?: ((event: React.KeyboardEvent) => void) | null
    recheckConditions: () => void
    shoudListenForConditions: boolean
    type: string
    value?: any
}

const CompanySearchField = React.forwardRef<any, CompanySearchFieldProps>(
    (props, ref) => {
        const [fieldValue, setFieldValue] = React.useState(props.value)
        const [showAdvancedSearch, setShowAdvancedSearch] =
            React.useState(false)
        const [advancedSearchData, setAdvancedSearchData] = React.useState({
            naam: '',
            kvkNummer: '',
            huisnummer: '',
            straatnaam: '',
            plaats: '',
            vestigingsnummer: '',
            postcode: '',
        })
        const [searchResults, setSearchResults] = React.useState<any[]>([])
        const [isSearching, setIsSearching] = React.useState(false)
        const innerRef = React.useRef<any>(null)
        const {
            companies,
            pagination,
            autocomplete,
            searchCompanies,
            goToPage,
            changePageSize,
            resetPagination,
        } = useCompany()

        React.useImperativeHandle(ref, () => ({
            getValue: () => fieldValue,
            setVal: (name, value) => setFieldValue(value),
        }))

        const onSelect = (itemSelected) => {
            const [idx, value] = itemSelected.split('-')

            setFieldValue(value)
            setTimeout(() => {
                const company = companies[idx] || null
                props?.changeValue(props.item.id, company, props.item)
            }, 100)
        }

        const onClear = () => {
            setFieldValue(null)
            setTimeout(() => {
                props?.changeValue(props.item.id, null, props.item)
            }, 100)
        }

        const onSearch = (searchValue: string) => {
            if (!props.item?.settings?.company_key) {
                return
            }

            const settings: Record<string, any> = {
                kvkNummer: searchValue,
            }

            if (Object.keys(settings).length > 0) {
                autocomplete({
                    kedo: props.kedo,
                    settings: settings,
                })
            }
        }

        const onChange = (value) => {
            setFieldValue(value)
        }

        const handleAdvancedSearch = () => {
            setShowAdvancedSearch(true)
        }

        const handleCloseAdvancedSearch = () => {
            setShowAdvancedSearch(false)
            setAdvancedSearchData({
                naam: '',
                kvkNummer: '',
                huisnummer: '',
                straatnaam: '',
                plaats: '',
                vestigingsnummer: '',
                postcode: '',
            })
        }

        const handleAdvancedSearchSubmit = async () => {
            setIsSearching(true)
            resetPagination()

            const searchCriteria: Record<string, any> = {}

            if (advancedSearchData.naam)
                searchCriteria.naam = advancedSearchData.naam
            if (advancedSearchData.kvkNummer)
                searchCriteria.kvkNummer = advancedSearchData.kvkNummer
            if (advancedSearchData.huisnummer)
                searchCriteria.huisnummer = advancedSearchData.huisnummer
            if (advancedSearchData.straatnaam)
                searchCriteria.straatnaam = advancedSearchData.straatnaam
            if (advancedSearchData.plaats)
                searchCriteria.plaats = advancedSearchData.plaats
            if (advancedSearchData.vestigingsnummer)
                searchCriteria.vestigingsnummer =
                    advancedSearchData.vestigingsnummer
            if (advancedSearchData.postcode)
                searchCriteria.postcode = advancedSearchData.postcode

            if (Object.keys(searchCriteria).length > 0) {
                try {
                    const result = await searchCompanies({
                        kedo: props.kedo,
                        settings: searchCriteria,
                        page: 1,
                        resultatenPerPagina: pagination.resultatenPerPagina,
                    })

                    setSearchResults(result.companies)
                } catch (error) {
                    console.error('Search error:', error)
                    setSearchResults([])
                }
            }
            setIsSearching(false)
        }

        const handleCompanySelect = (company: any) => {
            const companyValue = `${company.kvkNummer}-${company.naam}-${company.vestigingsnummer}`
            setFieldValue(companyValue)
            setTimeout(() => {
                props?.changeValue(props.item.id, company, props.item)
            }, 100)
            handleCloseAdvancedSearch()
        }

        const handleAdvancedSearchInputChange = (
            field: string,
            value: string
        ) => {
            setAdvancedSearchData((prev) => ({
                ...prev,
                [field]: value,
            }))
        }

        const handlePageChange = async (page: number) => {
            const searchCriteria: Record<string, any> = {}

            if (advancedSearchData.naam)
                searchCriteria.naam = advancedSearchData.naam
            if (advancedSearchData.kvkNummer)
                searchCriteria.kvkNummer = advancedSearchData.kvkNummer
            if (advancedSearchData.huisnummer)
                searchCriteria.huisnummer = advancedSearchData.huisnummer
            if (advancedSearchData.straatnaam)
                searchCriteria.straatnaam = advancedSearchData.straatnaam
            if (advancedSearchData.plaats)
                searchCriteria.plaats = advancedSearchData.plaats
            if (advancedSearchData.vestigingsnummer)
                searchCriteria.vestigingsnummer =
                    advancedSearchData.vestigingsnummer
            if (advancedSearchData.postcode)
                searchCriteria.postcode = advancedSearchData.postcode

            if (Object.keys(searchCriteria).length > 0) {
                const result = await goToPage(page, props.kedo, searchCriteria)
                setSearchResults(result.companies)
            }
        }

        const handleResultatenPerPaginaChange = async (newPerPage: number) => {
            const searchCriteria: Record<string, any> = {}

            if (advancedSearchData.naam)
                searchCriteria.naam = advancedSearchData.naam
            if (advancedSearchData.kvkNummer)
                searchCriteria.kvkNummer = advancedSearchData.kvkNummer
            if (advancedSearchData.huisnummer)
                searchCriteria.huisnummer = advancedSearchData.huisnummer
            if (advancedSearchData.straatnaam)
                searchCriteria.straatnaam = advancedSearchData.straatnaam
            if (advancedSearchData.plaats)
                searchCriteria.plaats = advancedSearchData.plaats
            if (advancedSearchData.vestigingsnummer)
                searchCriteria.vestigingsnummer =
                    advancedSearchData.vestigingsnummer

            if (Object.keys(searchCriteria).length > 0) {
                const result = await changePageSize(
                    newPerPage,
                    props.kedo,
                    searchCriteria
                )
                setSearchResults(result.companies)
            }
        }

        return (
            <>
                <Row>
                    <Col md={10} className="pl-3">
                        <SelectRC
                            ref={innerRef}
                            className="w-100 custom-select-react"
                            allowClear
                            placeholder={props.kedo.t('Search')}
                            value={fieldValue}
                            mode="combobox"
                            onChange={onChange}
                            onSearch={onSearch}
                            onSelect={onSelect}
                            onClear={onClear}
                            filterOption={false}
                            suffixIcon={
                                <FontAwesomeIcon size="xl" icon={faCaretDown} />
                            }
                            notFoundContent={props.kedo.t('No items found')}
                        >
                            {companies.map((company, idx) => (
                                <Option
                                    key={`${idx}-${company.kvkNummer}`}
                                    value={`${idx}-${company.kvkNummer}`}
                                    label={company.kvkNummer}
                                >
                                    {`${idx}-${company.kvkNummer} - ${company.naam} - ${company.vestigingsnummer}`}
                                </Option>
                            ))}
                        </SelectRC>
                    </Col>
                    <Col md={2} className="pl-3 d-flex align-items-center">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handleAdvancedSearch}
                            className="d-flex align-items-center"
                        >
                            <FontAwesomeIcon icon={faSearch} className="mr-1" />
                            {props.kedo.t('Advanced')}
                        </Button>
                    </Col>
                </Row>

                <Modal
                    show={showAdvancedSearch}
                    onHide={handleCloseAdvancedSearch}
                    size="xl"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {props.kedo.t('Advanced search')}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form className="mb-4">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    {props.kedo.t('company.KVK_Number')}
                                </Form.Label>

                                <Form.Control
                                    type="text"
                                    value={advancedSearchData.kvkNummer}
                                    onChange={(e) =>
                                        handleAdvancedSearchInputChange(
                                            'kvkNummer',
                                            e.target.value
                                        )
                                    }
                                    placeholder={
                                        props.kedo.t('company.Enter') +
                                        ' ' +
                                        props.kedo.t('company.KVK_Number')
                                    }
                                />
                            </Form.Group>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {props.kedo.t(
                                                'company.Company_Name'
                                            )}
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={advancedSearchData.naam}
                                            onChange={(e) =>
                                                handleAdvancedSearchInputChange(
                                                    'naam',
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                props.kedo.t('company.Enter') +
                                                ' ' +
                                                props.kedo.t(
                                                    'company.Company_Name'
                                                )
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {props.kedo.t(
                                                'company.Establishment_Number'
                                            )}
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={
                                                advancedSearchData.vestigingsnummer
                                            }
                                            onChange={(e) =>
                                                handleAdvancedSearchInputChange(
                                                    'vestigingsnummer',
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                props.kedo.t('company.Enter') +
                                                ' ' +
                                                props.kedo.t(
                                                    'company.Establishment_Number'
                                                )
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {props.kedo.t(
                                                'company.Street_Name'
                                            )}
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={
                                                advancedSearchData.straatnaam
                                            }
                                            onChange={(e) =>
                                                handleAdvancedSearchInputChange(
                                                    'straatnaam',
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                props.kedo.t('company.Enter') +
                                                ' ' +
                                                props.kedo.t(
                                                    'company.Street_Name'
                                                )
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {props.kedo.t('company.Postcode')}
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={advancedSearchData.postcode}
                                            onChange={(e) =>
                                                handleAdvancedSearchInputChange(
                                                    'postcode',
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                props.kedo.t('company.Enter') +
                                                ' ' +
                                                props.kedo.t('company.Postcode')
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {props.kedo.t(
                                                'company.House_Number'
                                            )}
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={
                                                advancedSearchData.huisnummer
                                            }
                                            onChange={(e) =>
                                                handleAdvancedSearchInputChange(
                                                    'huisnummer',
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                props.kedo.t('company.Enter') +
                                                ' ' +
                                                props.kedo.t(
                                                    'company.House_Number'
                                                )
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {props.kedo.t('company.City')}
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={advancedSearchData.plaats}
                                            onChange={(e) =>
                                                handleAdvancedSearchInputChange(
                                                    'plaats',
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                props.kedo.t('company.Enter') +
                                                ' ' +
                                                props.kedo.t('company.City')
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form>

                        {(isSearching || pagination.isLoading) && (
                            <div className="text-center mb-3">
                                <LoadingDefault size={'xl'} />
                            </div>
                        )}

                        {searchResults.length > 0 && (
                            <div>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5>
                                        {props.kedo.t('Search Results')} (
                                        {pagination.totalResults}{' '}
                                        {pagination.totalResults === 1
                                            ? props.kedo.t('result')
                                            : props.kedo.t('results')}
                                        )
                                    </h5>
                                    <div className="d-flex align-items-center">
                                        <span className="me-2">
                                            {props.kedo.t('Show')}:
                                        </span>
                                        <Form.Control
                                            as="select"
                                            value={
                                                pagination.resultatenPerPagina
                                            }
                                            onChange={(e) =>
                                                handleResultatenPerPaginaChange(
                                                    Number(e.target.value)
                                                )
                                            }
                                            style={{ width: 'auto' }}
                                            className="me-2"
                                            disabled={pagination.isLoading}
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                        </Form.Control>
                                        <span className="me-2">
                                            {props.kedo.t('per page')}
                                        </span>
                                    </div>
                                </div>

                                <div
                                    style={{
                                        maxHeight: '400px',
                                        overflowY: 'auto',
                                    }}
                                >
                                    <Table striped bordered hover>
                                        <thead className="table-dark sticky-top">
                                            <tr>
                                                <th>
                                                    {props.kedo.t(
                                                        'company.KVK_Number'
                                                    )}
                                                </th>
                                                <th>
                                                    {props.kedo.t(
                                                        'company.Company_Name'
                                                    )}
                                                </th>
                                                <th>
                                                    {props.kedo.t(
                                                        'company.Type'
                                                    )}
                                                </th>
                                                <th>
                                                    {props.kedo.t(
                                                        'company.Establishment_Number'
                                                    )}
                                                </th>
                                                <th>
                                                    {props.kedo.t(
                                                        'company.Full_address'
                                                    )}
                                                </th>
                                                <th>
                                                    {props.kedo.t(
                                                        'company.City'
                                                    )}
                                                </th>
                                                <th>
                                                    {props.kedo.t('Actions')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {searchResults.map(
                                                (company, index) => (
                                                    <tr
                                                        key={
                                                            (pagination.currentPage -
                                                                1) *
                                                                pagination.resultatenPerPagina +
                                                            index
                                                        }
                                                    >
                                                        <td>
                                                            {company.kvkNummer}
                                                        </td>
                                                        <td>{company.naam}</td>
                                                        <td>
                                                            {company.type ||
                                                                'N/A'}
                                                        </td>
                                                        <td>
                                                            {
                                                                company.vestigingsnummer
                                                            }
                                                        </td>
                                                        <td>
                                                            {company.volledigAdres ||
                                                                `${company.straatnaam} ${company.huisnummer}, ${company.plaats}`}
                                                        </td>
                                                        <td>
                                                            {company.plaats}
                                                        </td>
                                                        <td>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() =>
                                                                    handleCompanySelect(
                                                                        company
                                                                    )
                                                                }
                                                            >
                                                                {props.kedo.t(
                                                                    'Select'
                                                                )}
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                                {pagination.totalResults >
                                    pagination.resultatenPerPagina && (
                                    <Pagination
                                        pager={{
                                            pages: pagination.totalPages,
                                            current: pagination.currentPage - 1,
                                        }}
                                        onChangePage={(pageIndex) =>
                                            handlePageChange(pageIndex + 1)
                                        }
                                    />
                                )}

                                {pagination.totalResults > 0 && (
                                    <div className="text-center text-muted mt-2">
                                        {props.kedo.t('Showing')}{' '}
                                        {(pagination.currentPage - 1) *
                                            pagination.resultatenPerPagina +
                                            1}
                                        -
                                        {Math.min(
                                            pagination.currentPage *
                                                pagination.resultatenPerPagina,
                                            pagination.totalResults
                                        )}{' '}
                                        {props.kedo.t('of')}{' '}
                                        {pagination.totalResults}{' '}
                                        {props.kedo.t('results')}
                                    </div>
                                )}
                            </div>
                        )}

                        {searchResults.length === 0 && !isSearching && (
                            <div className="text-center text-muted">
                                <p>{props.kedo.t('search_criteria')}</p>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={handleCloseAdvancedSearch}
                        >
                            {props.kedo.t('Cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleAdvancedSearchSubmit}
                            disabled={isSearching || pagination.isLoading}
                        >
                            {isSearching
                                ? props.kedo.t('Searching...')
                                : props.kedo.t('Search')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        )
    }
)

CompanySearchField.displayName = 'CompanySearchField'

export default CompanySearchField
